import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Loader2, Package, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { purchasingAPI, coreAPI, suppliersAPI, fetchAllPages, CommodityGroup, CommodityType, Site, CompanyGroup, SupplierProfile, DeliveryAppeal } from '../../../lib/api';
import { toast } from 'sonner';
import DeliveryDetailsModal from '../modals/DeliveryDetailsModal';
import RaiseAppealModal from '../modals/RaiseAppealModal';
import AppealResponseModal from '../modals/AppealResponseModal';
import { AcknowledgeGRNModal, type GRNWorkflowData } from '../GRNWorkflowModals';
import { DeliveryFilterSection } from './DeliveryFilterSection';
import { useIsSupplier } from '../../../hooks/usePortalMode';
import { useFilterVisibility } from '../../../hooks/useFilterVisibility';
import UnallocatedGRNsTab from './UnallocatedGRNsTab';
import LinkScheduleModal from '../modals/LinkScheduleModal';
import { Pagination } from '../../ui/pagination';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { SectionTourTrigger } from '../../tour/SectionTourTrigger';
import { GoodsReceiptNote } from '../../../lib/api/types/grn-types';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { useAuth } from '../../../contexts/AuthContext';
import MySuppliersToggle from '../MySuppliersToggle';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { useView } from '../../../contexts/ViewContext';
// CR-6442 rework: same country-option derivation the dashboard uses, so the
// two screens offer identical Country lists rather than diverging copies.
import { deriveCountryOptions } from '../../dashboard/dashboardFilterUtils';
import {
  DeliveryColumnConfig,
  DEFAULT_DELIVERY_COLUMNS,
  loadDeliveryColumns,
  saveDeliveryColumns,
  filterDeliveryCsvByVisibleColumns,
} from './delivery/deliveryColumnConfig';
import DeliveryColumnsDropdown from './delivery/DeliveryColumnsDropdown';
import DeliveryRow from './delivery/DeliveryRow';
import AppealsSubTab from './AppealsSubTab';
import { actionRequiredParams } from './appeals';
import { getAppealStatusBadge, getOntimeStatusBadge } from './delivery/deliveryBadges';
import DeliveryQuickSearch from './delivery/DeliveryQuickSearch';
import { fetchDeliveriesData, buildDeliveriesExportParams } from './delivery/deliveriesData';
import { readDeliveryUrlState, applyDeliveryUrlParams } from './delivery/deliveryFilterUrlSync';
import { isBulkAppealModeActive } from './delivery/bulkAppealMode';
import { useDeliveryBulkSelection } from './delivery/useDeliveryBulkSelection';
import DeliveryBulkBar from './delivery/DeliveryBulkBar';
import { pageCountFromResponse, type PageSizeMemory } from '../../../hooks/usePagination';


export default function DeliveryTab() {
  const { user } = useAuth();
  // Buyer-code scoping: default to "my suppliers" for users with codes.
  const buyerCodes = user?.buyer_codes ?? [];
  const [mySuppliersOnly, setMySuppliersOnly] = useState(true);
  const isSupplier = user?.platform === 'SUPPLIER';
  const isHelixUser = user?.company_type === 'helix';
  // CR-6450: the reworked filter layout is scoped to the supplier VIEW, which
  // for a dual-portal ('both') company is the active view rather than the
  // account's platform. Kept separate from `isSupplier` above so the existing
  // Open-Orders visibility gates keep their current meaning.
  const isSupplierView = useIsSupplier();
  // Schedule-aware tenants: On-Time Status at PO-line / GRN level is
  // undefined because one GRN can span multiple schedules with
  // different receipt-vs-schedule-date verdicts. Per-allocation OTIF
  // lives inside the Delivery modal's Schedule Allocation tab. Hide
  // the column here so the grid doesn't imply a single-value answer.
  const { isFeatureEnabled, isOpenOrdersControlHidden, deliveryAppealBulkEnabled, isDashboardTabEnabled, isLoading: menuSettingsLoading } = useMenuSettings();
  // Customer review 11 Sep (item 37/39): the GRN-to-schedule repair tools
  // (Unallocated GRNs tab + per-row "Re-assign to schedule line") only exist
  // for the scheduling module. Mirror the backend ``_require_repair_access``
  // gate exactly — a Scheduled-enabled install is a precondition for
  // everyone, superusers included — so a PO-line tenant (e.g. the reviewed tenant) never sees
  // a control the server would 403. Within a scheduled install, superusers
  // and Django staff qualify (same rule as the Sidebar's GRN Repair link).
  // CR-6274 adds the module switch in front of the same rule, so switching
  // GRN Repair off in Platform Admin also removes the Unallocated GRNs tab
  // and the per-row re-assign action here.
  const hasGrnRepairAccess = isFeatureEnabled('grn_repair')
    && isDashboardTabEnabled('scheduled')
    && (!!user?.is_superuser || !!user?.is_staff);
  // CR-5884: Open Orders supplier-facing hide-toggles. The Deliveries view
  // is part of the supplier-facing "Open Orders" experience, so the same
  // admin toggles apply here. Customer / Helix viewers always see controls.
  const hideActionsForSupplier = isOpenOrdersControlHidden('hide_actions', isSupplier);
  const hideAppealForSupplier = isOpenOrdersControlHidden('hide_otif_override_button', isSupplier);
  // CR-7420: the bulk-appeal gate is keyed on the supplier VIEW, so its copy of
  // this control has to be too. isOpenOrdersControlHidden short-circuits to
  // false whenever its isSupplier argument is false, and a dual-portal ('both')
  // company is never company_type 'supplier' — so the platform-keyed value
  // above is forced false for them even while they are acting as a supplier,
  // which would hand them a bulk bar the admin's toggle could not suppress.
  // The single-row path keeps the platform-keyed value: there the branch
  // condition and the control are fed the same signal, so they cannot diverge.
  const hideAppealForSupplierView = isOpenOrdersControlHidden('hide_otif_override_button', isSupplierView);
  const scheduleCollabActive = isFeatureEnabled('po_line_schedules');
  // Customer review 11 Sep (item 37/39): re-assign additionally needs the
  // active customer's delivery grain to be schedule lines — on a PO-line
  // customer there is no schedule line to re-assign to. Acknowledge + the
  // one-click Appeal icons in the Action column are unaffected.
  const canReassign = hasGrnRepairAccess && scheduleCollabActive;
  // Unallocated GRNs follows the same rule: only for a customer whose delivery
  // grain is schedule lines — a PO-line customer has no schedule slot to
  // allocate a GRN to.
  const canSeeUnallocated = hasGrnRepairAccess && scheduleCollabActive;
  // CR-4665 rework: Honour the active view-context scope so the deliveries
  // list refreshes when the user switches supplier/site/group via the
  // top-right selector. Previously this tab ignored ViewContext entirely
  // and the backend fell back to all accessible POs — Sarah saw Northstar
  // GRNs while the selector said Skybridge.
  const { viewKey, getViewQueryParams } = useView();
  const [searchParams, setSearchParams] = useSearchParams();
  // CR-7106: hydrate the FULL applied filter state from the URL once, on
  // mount (covers the legacy deep-link params plus every other filter,
  // quick tab, sort and page size). The lazy useState initializer runs once,
  // so later URL rewrites by the sync effect never re-hydrate.
  const [urlState] = useState(() => readDeliveryUrlState(new URLSearchParams(window.location.search)));
  const [grns, setGrns] = useState<GoodsReceiptNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(urlState.activeTab || 'all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const servedPageRef = useRef<PageSizeMemory | undefined>(undefined);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [selectedGrn, setSelectedGrn] = useState<GoodsReceiptNote | null>(null);
  // Superuser-only: re-assign ANY GRN (allocated or not) to a schedule line.
  const [reassignGrn, setReassignGrn] = useState<GoodsReceiptNote | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [appealGrn, setAppealGrn] = useState<GoodsReceiptNote | null>(null);
  // CR-4660: Direct acknowledge modal (skip details popup)
  const [acknowledgeGrn, setAcknowledgeGrn] = useState<GoodsReceiptNote | null>(null);
  // CR-4786: Respond-to-appeal modal opened from a GRN row whose latest
  // appeal is awaiting customer response (supplier raised it, customer
  // needs to Accept / Query Supplier / Reject — not re-Acknowledge).
  const [respondAppeal, setRespondAppeal] = useState<DeliveryAppeal | null>(null);
  // Force AppealsSubTab to refetch its rows + tab counts whenever the parent
  // mutates appeal state (Accept / Reject / Query / Cancel). The subtab owns
  // its own data, so a parent fetchData() doesn't reach it — bumping this
  // counter is what flips the row's badge & action column without a hard
  // refresh.
  const [appealsRefreshKey, setAppealsRefreshKey] = useState(0);
  // CR-7167: Action required count for the "Submitted Appeals" button badge.
  // Shows how many supplier-raised appeals are awaiting customer response
  // (for customer view) or customer-raised awaiting supplier (for supplier view).
  const [appealsActionCount, setAppealsActionCount] = useState(0);
  const [exporting, setExporting] = useState(false);  // CR-3861: Export state
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [sortField, setSortField] = useState(urlState.sortField || 'receipt_date');  // Default sort by delivery date
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(urlState.sortDirection || 'desc');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [pageSize, setPageSize] = useState(urlState.pageSize || 50);

  // Config-driven column visibility + ordering, persisted per user (mirrors
  // the Schedule Lines column manager).
  const userId = user?.id;
  const [columns, setColumns] = useState<DeliveryColumnConfig[]>(() => loadDeliveryColumns(userId));

  // Read URL search params for deep linking
  // CR-7106: deep-link params (grnno / ponumber / linenumber / appealstatus
  // + legacy aliases search / grnstatus) are read by readDeliveryUrlState
  // below; only the auto-open GRN needs its raw value here.
  const urlGrnNo = searchParams.get('grnno');
  const autoOpenGrn = useRef<string | null>(urlGrnNo);
  // CR-7167: Read subtab from URL for deep-linking from dashboard
  const urlSubtab = searchParams.get('subtab');
  // CR-7167: Read appealtab from URL to pre-select Action Required tab.
  // Uses 'appealtab' (not 'appealstatus') to avoid deliveryFilterUrlSync
  // mapping it to the deliveries API filter.
  const urlAppealTab = searchParams.get('appealtab');

  // Text input states (for debouncing)
  // CR-7167: Initialize mainSubTab from URL param if present (for dashboard click-through)
  const [mainSubTab, setMainSubTab] = useState<'deliveries' | 'appeals' | 'unallocated'>(() => {
    if (urlSubtab === 'appeals') return 'appeals';
    if (urlSubtab === 'unallocated') return 'unallocated';
    return 'deliveries';
  });
  // Switching to a PO-line customer while on Unallocated GRNs would leave no
  // sub-tab selected; fall back to Deliveries once settings have loaded.
  useEffect(() => {
    if (!menuSettingsLoading && mainSubTab === 'unallocated' && !canSeeUnallocated) {
      setMainSubTab('deliveries');
    }
  }, [menuSettingsLoading, mainSubTab, canSeeUnallocated]);

  const [textInputs, setTextInputs] = useState({
    grnNumber: urlState.filters['grnNumber'] || '',
    poNumber: urlState.filters['poNumber'] || '',
    supplier: urlState.filters['supplier'] || '',
    supplierName: urlState.filters['supplierName'] || '',
    partNumber: urlState.filters['partNumber'] || '',
    lineNumber: urlState.filters['lineNumber'] || '',
  });

  // CR-6364: pending date filters, kept OUT of `textInputs` so the text-input
  // debounce never fires (and re-queries) on a date change. Applied to `filters`
  // only when the user clicks Search — so picking From/To no longer reloads the
  // grid after each individual selection.
  const [pendingDates, setPendingDates] = useState({
    dateFrom: urlState.filters['dateFrom'] || '',
    dateTo: urlState.filters['dateTo'] || '',
    oddFrom: urlState.filters['oddFrom'] || '',
    oddTo: urlState.filters['oddTo'] || '',
  });

  // Actual filter state (used for API calls)
  const [filters, setFilters] = useState({
    grnNumber: urlState.filters['grnNumber'] || '',
    poNumber: urlState.filters['poNumber'] || '',
    supplier: urlState.filters['supplier'] || '',
    supplierName: urlState.filters['supplierName'] || '',
    partNumber: urlState.filters['partNumber'] || '',
    lineNumber: urlState.filters['lineNumber'] || '',
    inspection_status: urlState.filters['inspection_status'] || '',
    dateFrom: urlState.filters['dateFrom'] || '',
    dateTo: urlState.filters['dateTo'] || '',
    site: urlState.filters['site'] || '',
    warehouse: urlState.filters['warehouse'] || '',
    supplierGroup: urlState.filters['supplierGroup'] || '',
    commodityGroup: urlState.filters['commodityGroup'] || '',
    commodityType: urlState.filters['commodityType'] || '',
    appealStatus: urlState.filters['appealStatus'] || '',
    appealFilter: urlState.filters['appealFilter'] || '',
    poLineStatus: urlState.filters['poLineStatus'] || '',
    grnStatus: urlState.filters['grnStatus'] || '',
    oddFrom: urlState.filters['oddFrom'] || '',
    oddTo: urlState.filters['oddTo'] || '',
    strategic: urlState.filters['strategic'] || '',
    supplierCode: urlState.filters['supplierCode'] || '',
    // CR-6442 rework: the filters the design called for that the first pass
    // deferred. Each one now has a backing lookup on the GRN filterset, so a
    // selection actually narrows the grid rather than rendering as a dead
    // control.
    customerCountry: urlState.filters['customerCountry'] || '',
    division: urlState.filters['division'] || '',
    supplierCountry: urlState.filters['supplierCountry'] || '',
    supplierType: urlState.filters['supplierType'] || '',
    buyerCode: urlState.filters['buyerCode'] || '',
    strategicOnly: urlState.filters['strategicOnly'] || '',
  });

  // CR-4802: a URL (bookmark, shared link) can carry a value for a filter the
  // tenant hides. The control never renders, so the value could otherwise be
  // sent and never cleared. Once visibility is known, drop hidden values.
  const fv = useFilterVisibility();
  useEffect(() => {
    if (!fv.ready) return;
    const hidden: Array<[keyof typeof filters, boolean]> = [
      ['customerCountry', fv.companyCountry],
      ['division', fv.division],
      ['warehouse', fv.warehouse],
      ['supplierCode', fv.supplierCode],
      ['supplierType', fv.supplierType],
      ['commodityGroup', fv.commodity],
      ['commodityType', fv.commodity],
    ];
    setFilters((prev) => {
      let next = prev;
      for (const [key, visible] of hidden) {
        if (!visible && prev[key]) next = { ...next, [key]: '' };
      }
      return next;
    });
  }, [fv.ready, fv.companyCountry, fv.division, fv.warehouse, fv.supplierCode, fv.supplierType, fv.commodity]);

  const [siteOptions, setSiteOptions] = useState<{ value: string; label: string; group?: number | null; country?: string }[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<{ value: string; label: string }[]>([]);
  const [supplierCountryOptions, setSupplierCountryOptions] = useState<string[]>([]);
  const [supplierGroupOptions, setSupplierGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityGroupOptions, setCommodityGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityTypeOptions, setCommodityTypeOptions] = useState<{ value: string; label: string; group?: number }[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<{ value: string; label: string; country?: string }[]>([]);

  // Guard: a URL deep-link like ?tab=early_late or ?tab=on_time would
  // otherwise silently land on a now-hidden tab on schedule-aware
  // tenants. Coerce to 'all' when that happens.
  useEffect(() => {
    // Only 'on_time' is hidden on schedule-aware tenants now; 'early_late' is
    // an always-visible quick filter (Early & Late), so it must not be coerced.
    if (scheduleCollabActive && activeTab === 'on_time') {
      setActiveTab('all');
    }
  }, [scheduleCollabActive, activeTab]);

  // CR-7106: filters now PERSIST in the URL (extending the CR-5769 pattern
  // from PO Lines / Quality.tsx to this list) — superseding CR-4580's
  // always-start-fresh behaviour, whose mount-time param strip lived here.
  // Hydration happens in the state initializers above; this effect only
  // opens the panel when any filter arrived via URL.
  useEffect(() => {
    if (urlState.hasFilterParams) {
      setShowFilters(true);
    }
    setInitialLoadComplete(true);

    // A site switch starts a new load while the supplier walk may still be
    // running; only the newest load may write the options.
    let cancelled = false;
    // Fetch filter options
    Promise.all([
      coreAPI.getAccessibleSites().catch(() => ({ data: [] })),
      purchasingAPI.getCommodityGroups({ enabled: true }).catch(() => ({ data: [] })),
      purchasingAPI.getCommodityTypes({ enabled: true }).catch(() => ({ data: [] })),
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      // Supplier groups are a customer-side filter; the endpoint 403s for
      // supplier users, so don't ask.
      // The unfiltered list is Helix-only (403 for everyone else) — customer
      // users read their traded suppliers' groups from the scoped for-filter
      // endpoint instead of asking and swallowing the 403.
      isSupplierView
        ? Promise.resolve([] as CompanyGroup[])
        : isHelixUser
          ? fetchAllPages<CompanyGroup>((page) => coreAPI.getCompanyGroups({ page_size: 100, page, company_type: 'supplier' })).catch(() => [] as CompanyGroup[])
          : coreAPI.getSupplierGroupsForFilter().then((res) => res.data.results || []).catch(() => [] as CompanyGroup[]),
      // minimal skips the per-supplier performance aggregates — without it
      // this call times out on large installs and the dropdown reads empty.
      // Walked too: the supplier cap is 1000 a page.
      fetchAllPages<SupplierProfile>((page) => (cancelled
        ? Promise.reject(new Error('Superseded by a newer filter load'))
        : suppliersAPI.getProfiles({ page_size: 1000, page, is_active: true, minimal: true }))).catch(() => [] as SupplierProfile[]),
      // CR-6442 rework: divisions are customer-side company groups — the same
      // source the dashboard's Division dropdown reads, so both screens offer
      // the same list. Paged for the same reason as the supplier groups above.
      // Same Helix-only gate as above: no scoped divisions endpoint exists
      // for other roles, so don't fire a request that can only 403.
      isHelixUser
        ? fetchAllPages<CompanyGroup>((page) => coreAPI.getCompanyGroups({ page_size: 100, page, company_type: 'customer' })).catch(() => [] as CompanyGroup[])
        : Promise.resolve([] as CompanyGroup[]),
    ]).then(([sitesRes, groupsRes, typesRes, companyGroups, supplierPages, divisions]) => {
      if (cancelled) return;
      const sites = (Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data as unknown as { results: Site[] }).results || []) as Site[];
      setSiteOptions(sites.map((s) => ({ value: String(s.id), label: s.name, group: s.group_id ?? null, country: s.country })));
      const groups = Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data as unknown as { results: CommodityGroup[] }).results || [];
      setCommodityGroupOptions(groups.filter((g: CommodityGroup) => g.enabled).map((g: CommodityGroup) => ({ value: String(g.id), label: g.name })));
      const types = Array.isArray(typesRes.data) ? typesRes.data : (typesRes.data as unknown as { results: CommodityType[] }).results || [];
      // CR-6442 rework: carry the parent group so Commodity Type can narrow to
      // the selected Commodity Group, which the AC asks for and the panel only
      // ever did as a blind reset.
      setCommodityTypeOptions(types.filter((t: CommodityType) => t.enabled).map((t: CommodityType) => ({ value: String(t.id), label: t.name, group: t.commodity_group })));
      setSupplierGroupOptions(companyGroups.map((g: CompanyGroup) => ({ value: String(g.id), label: g.name })));
      // Each page is cached on its own, so one fetched later can repeat a row.
      const suppliers = Array.from(new Map(supplierPages.map((s) => [s.id, s])).values());
      // The supplier company id: `minimal` responses carry it as company_id,
      // the full serializer as both. Reading `company` alone produced the
      // literal string "undefined" for every option once this call moved to
      // minimal — the filter then failed with "Select a valid choice".
      // Options with no resolvable id are dropped rather than offered as a
      // choice that cannot work.
      setSupplierOptions(suppliers.flatMap((s) => {
        const companyId = s.company_id ?? s.company;
        if (companyId === undefined || companyId === null) return [];
        return [{
          value: String(companyId),
          label: s.company_name,
          // Carried so Supplier Country can narrow Supplier Site, per the AC.
          country: s.company_details?.country,
        }];
      }));
      setDivisionOptions(divisions.map((g: CompanyGroup) => ({ value: String(g.id), label: g.name })));
      // Supplier Country lists the values actually present on the suppliers in
      // scope, the same way the dashboard derives it — a fixed ISO list would
      // offer countries no supplier is in.
      setSupplierCountryOptions(
        deriveCountryOptions(suppliers.map((s) => ({ country: s.company_details?.country })))
      );
    });
    return () => { cancelled = true; };
  }, [viewKey]); // supplier-group options follow the site switcher

  // Debounce text inputs
  useEffect(() => {
    if (!initialLoadComplete) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        grnNumber: textInputs.grnNumber,
        poNumber: textInputs.poNumber,
        supplier: textInputs.supplier,
        supplierName: textInputs.supplierName,
        partNumber: textInputs.partNumber,
        lineNumber: textInputs.lineNumber,
      }));
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [textInputs, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete) {
      fetchData();
    }
    // CR-4665 rework: viewKey changes when the user switches scope via
    // the top-right selector — re-fetch so deliveries reflect the new
    // company/site/group instead of the previous one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, activeTab, initialLoadComplete, sortField, sortDirection, pageSize, viewKey, mySuppliersOnly]);

  // CR-7167: Fetch the "action required" count for the Submitted Appeals badge.
  // This is a cheap call (page_size=1) that just reads the .count off the queryset.
  // Re-fetches when the appeals list changes (appealsRefreshKey) or scope changes.
  // Includes view context params so single-site admins don't see group-wide numbers.
  useEffect(() => {
    const fetchAppealsActionCount = async () => {
      try {
        // CR-8239: the appeals waiting on the viewer's side, as the Action
        // Required tab counts them (a supplier's are the ones the customer queried).
        const viewParams = getViewQueryParams();
        const params: Record<string, string | number> = {
          ...actionRequiredParams(isSupplierView),
          page_size: 1,
          page: 1,
        };
        // Add view context (site/group/company) so scope matches the user's selection
        for (const [k, v] of Object.entries(viewParams)) {
          if (v !== undefined) params[k] = v;
        }
        const response = await purchasingAPI.getDeliveryAppeals(params);
        const data = response.data;
        if (data && typeof data === 'object' && 'count' in data) {
          setAppealsActionCount((data as { count: number }).count);
        }
      } catch (err) {
        console.error('Failed to fetch appeals action count:', err);
      }
    };
    fetchAppealsActionCount();
  }, [appealsRefreshKey, viewKey, isSupplierView, getViewQueryParams]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const { results, count, tabCounts } = await fetchDeliveriesData({
        filters,
        activeTab,
        sortField,
        sortDirection,
        currentPage,
        pageSize,
        mySuppliersOnly,
        buyerCodes,
        viewParams: getViewQueryParams(),
      });
      setGrns(results);
      setTotalCount(count);
      // CR-7279: page count from what the server served, not what was asked for.
      const paged = pageCountFromResponse(count, pageSize, results.length, currentPage, servedPageRef.current);
      servedPageRef.current = paged.memory;
      setTotalPages(paged.totalPages);
      setTabCounts({ ...tabCounts });

      // Auto-open modal for URL deep-linked GRN
      if (autoOpenGrn.current && results.length > 0) {
        const match = results.find(
          (g: GoodsReceiptNote) => g.grn_number === autoOpenGrn.current,
        );
        if (match) {
          setSelectedGrn(match);
          setShowModal(true);
        }
        autoOpenGrn.current = null;
      }
    } catch (err: unknown) {
      console.error('Error fetching GRN data:', err);
      setError(getApiErrorMessage(err, 'Failed to load GRN data'));
    } finally {
      setLoading(false);
    }
  };

  // Ref to always access the latest fetchData without adding it to useCallback deps
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  const clearFilters = () => {
    setTextInputs({
      grnNumber: '',
      poNumber: '',
      supplier: '',
      supplierName: '',
      partNumber: '',
      lineNumber: '',
    });
    setPendingDates({ dateFrom: '', dateTo: '', oddFrom: '', oddTo: '' });
    setFilters({
      grnNumber: '',
      poNumber: '',
      supplier: '',
      supplierName: '',
      partNumber: '',
      lineNumber: '',
      inspection_status: '',
      dateFrom: '',
      dateTo: '',
      site: '',
      warehouse: '',
      supplierGroup: '',
      commodityGroup: '',
      commodityType: '',
      appealStatus: '',
      appealFilter: '',
      poLineStatus: '',
      grnStatus: '',
      oddFrom: '',
      oddTo: '',
      strategic: '',
      supplierCode: '',
      customerCountry: '',
      division: '',
      supplierCountry: '',
      supplierType: '',
      buyerCode: '',
      strategicOnly: '',
    });
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleFilterChange = (key: string, value: string) => {
    // CR-6364: date filters are pending-only — update the separate pendingDates
    // state (which nothing watches) and do NOT touch `filters` or reset the
    // page, so selecting a date never reloads the grid. Applied to `filters`
    // when the user clicks Search.
    if (['dateFrom', 'dateTo', 'oddFrom', 'oddTo'].includes(key)) {
      setPendingDates((prev) => ({ ...prev, [key]: value }));
      return;
    }
    // Text inputs: update textInputs state (will be debounced)
    if (['grnNumber', 'poNumber', 'supplier', 'supplierName', 'partNumber', 'lineNumber'].includes(key)) {
      setTextInputs((prev) => ({ ...prev, [key]: value }));
    } else {
      // Dropdowns and dates: update filters directly (instant) — use functional update
      // to avoid stale state when multiple updates happen in sequence
      if (key === 'commodityGroup') {
        setFilters((prev) => ({ ...prev, [key]: value, commodityType: '' }));
      } else if (key === 'customerCountry') {
        // CR-6442 rework: Country is a parent of Site (and Warehouse through
        // it). Drop a selection that no longer belongs to the chosen country
        // so the grid is never queried with an impossible combination —
        // the rule the dashboard already follows.
        setFilters((prev) => {
          const site = siteOptions.find((s) => s.value === prev.site);
          const stale = !!value && !!site && site.country !== value;
          return { ...prev, customerCountry: value, ...(stale ? { site: '', warehouse: '' } : {}) };
        });
      } else if (key === 'division') {
        setFilters((prev) => {
          const site = siteOptions.find((s) => s.value === prev.site);
          const stale = !!value && !!site && String(site.group ?? '') !== value;
          return { ...prev, division: value, ...(stale ? { site: '', warehouse: '' } : {}) };
        });
      } else if (key === 'supplierCountry') {
        setFilters((prev) => {
          const sup = supplierOptions.find((s) => s.value === prev.supplier);
          const stale = !!value && !!sup && sup.country !== value;
          return { ...prev, supplierCountry: value, ...(stale ? { supplier: '' } : {}) };
        });
      } else {
        setFilters((prev) => ({ ...prev, [key]: value }));
      }
    }
    setCurrentPage(1);
  };

  // CR-6364: Search is the single apply action — push the pending text AND date
  // inputs into `filters` (which drives the fetch effect) and reset to page 1.
  const handleSearch = () => {
    // Cancel any in-flight text-input debounce so it can't fire after this and
    // re-apply the same values, causing a duplicate fetch.
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setCurrentPage(1);
    setFilters((prev) => ({
      ...prev,
      grnNumber: textInputs.grnNumber,
      poNumber: textInputs.poNumber,
      supplier: textInputs.supplier,
      supplierName: textInputs.supplierName,
      partNumber: textInputs.partNumber,
      lineNumber: textInputs.lineNumber,
      dateFrom: pendingDates.dateFrom,
      dateTo: pendingDates.dateTo,
      oddFrom: pendingDates.oddFrom,
      oddTo: pendingDates.oddTo,
    }));
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
    // Early & Late is defined as "latest deliveries first" — force the sort to
    // most-recent receipt date so the newest early/late arrivals lead.
    if (tab === 'early_late') {
      setSortField('receipt_date');
      setSortDirection('desc');
    }
  };

  // CR-7106: mirror the APPLIED state into the URL (replace, no history
  // entries) so a page refresh restores exactly what the user had. Pending
  // (unapplied) inputs deliberately stay out — the URL reflects the grid.
  useEffect(() => {
    if (!initialLoadComplete) return;
    // applyDeliveryUrlParams edits a COPY of the current params, deleting
    // only our own names — sibling tabs' persisted params survive, and the
    // legacy aliases (grnno &co) are removed after their one-mount
    // consumption so a reload can never re-trigger the grnno auto-open.
    setSearchParams(
      applyDeliveryUrlParams(
        new URLSearchParams(window.location.search),
        filters as unknown as Record<string, string>,
        { activeTab, sortField, sortDirection, pageSize },
      ),
      { replace: true },
    );
    // setSearchParams excluded; reading the live location keeps foreign
    // params fresh without re-firing on our own rewrite.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTab, sortField, sortDirection, pageSize, initialLoadComplete]);

  // Memoized handlers to prevent DeliveryRow re-renders
  const handleViewDetails = useCallback((grn: GoodsReceiptNote) => {
    setSelectedGrn(grn);
    setShowModal(true);
  }, []);

  // CR-4786: Open the Respond-to-Appeal modal for a GRN whose latest
  // appeal is awaiting customer response. The GRN row only carries a
  // thin latest_appeal stub (id/status/sequence); fetch the full appeal
  // so the modal can render claimed values, supplier note, etc.
  const openAppealResponseForGrn = useCallback(async (grn: GoodsReceiptNote) => {
    const appealId = grn.latest_appeal?.id;
    if (!appealId) {
      toast.error('No appeal to respond to on this delivery');
      return;
    }
    try {
      const res = await purchasingAPI.getDeliveryAppeal(appealId);
      setRespondAppeal(res.data);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load appeal details'));
    }
  }, []);

  // CR-4660: Direct acknowledge handler (skips DeliveryDetailsModal).
  // CR-4601 round 5: patch the row from the POST response immediately so
  // the Action + Appeal Status columns flip to "Acknowledged" without
  // waiting for the (unawaited) fetchData refetch — Sarah's QA hit a
  // window where the success toast fired but the buttons lingered
  // because the table re-rendered from stale state before the GET
  // returned. The endpoint already returns the updated GRN.
  const handleAcknowledgeSubmit = async (workflowData: GRNWorkflowData) => {
    const res = await purchasingAPI.acknowledgeGRN(workflowData.grnId, { comments: workflowData.comments });
    const updated = res.data?.data;
    if (updated && typeof updated === 'object' && 'id' in updated) {
      setGrns(prev => prev.map(g => g.id === updated.id ? { ...g, ...updated } : g));
    }
    toast.success('GRN acknowledged successfully');
    fetchData();
  };

  // CR-4869/4870: Supplier cancels their own active appeal from the
  // deliveries action column. The backend cancel endpoint only accepts
  // the creator's company (widened to the creator's whole group in
  // CR-4870 to match group-wide appeal visibility); unexpected
  // failures surface via toast.
  const handleCancelAppeal = useCallback(async (grn: GoodsReceiptNote, e: React.MouseEvent) => {
    e.stopPropagation();
    const appealId = grn.latest_appeal?.id;
    if (!appealId) return;
    if (!window.confirm('Cancel this appeal? This action cannot be undone.')) return;
    try {
      await purchasingAPI.cancelDeliveryAppeal(appealId);
      toast.success('Appeal cancelled');
      fetchDataRef.current();
      setAppealsRefreshKey(k => k + 1);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to cancel appeal'));
    }
  }, []);

  // Handle column sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  // ----- Column management (visibility + drag reorder) -----
  const toggleColumn = (id: string) => {
    setColumns((prev) => { const n = prev.map((c) => c.id === id ? { ...c, visible: !c.visible } : c); saveDeliveryColumns(n, userId); return n; });
  };
  const resetColumns = () => { setColumns(DEFAULT_DELIVERY_COLUMNS); saveDeliveryColumns(DEFAULT_DELIVERY_COLUMNS, userId); };
  const selectAllCols = () => { setColumns((p) => { const n = p.map((c) => ({ ...c, visible: true })); saveDeliveryColumns(n, userId); return n; }); };
  const reorderColumns = (from: number, to: number) => {
    setColumns((prev) => { const n = [...prev]; const [m] = n.splice(from, 1); n.splice(to, 0, m); const o = n.map((c, i) => ({ ...c, order: i })); saveDeliveryColumns(o, userId); return o; });
  };

  // Runtime gating: some columns only apply in certain contexts. Gated-out
  // columns are hidden from both the table and the column manager.
  const isColumnAvailable = (id: string): boolean => {
    if (id === 'action') return !hideActionsForSupplier;
    if (id === 'po_line_status') return !scheduleCollabActive;
    if (id === 'unit_price_converted' || id === 'total_value_converted') return !isSupplier;
    return true;
  };
  const availableIds = new Set(columns.filter((c) => isColumnAvailable(c.id)).map((c) => c.id));
  const visibleCols = columns.filter((c) => c.visible && availableIds.has(c.id));
  const tableMinWidth = visibleCols.reduce((sum, c) => sum + (parseInt(c.minWidth, 10) || 120), 0);

  // Bulk (multi-select) delivery appeals, both sides — see bulkAppealMode.ts
  // for why the supplier-only hide control does not gate the customer.
  //
  // Keyed on the supplier VIEW, not the account platform. The backend decides
  // the appeal's side with appeal_actor_is_customer(), which for anyone whose
  // company is not literally the PO's customer/supplier falls back to
  // current_site / current_group / profile.portal_mode — the active view. A
  // dual-portal ('both') company acting as supplier has platform !== SUPPLIER,
  // so keying on the platform would show them the customer's auto-accept copy
  // for an appeal the server parks at awaiting_response.
  const bulkModeActive = isBulkAppealModeActive(deliveryAppealBulkEnabled, isSupplierView, hideAppealForSupplierView);

  // CR-8214: the selection, and which selected rows each bulk action covers.
  const bulk = useDeliveryBulkSelection(grns, bulkModeActive, isSupplierView);
  const clearBulkSelection = bulk.clear;

  // Clear any bulk selection whenever the underlying list is re-queried
  // (page, filters, tab, sort, scope) so stale ids from a previous page never
  // leak into a bulk submit.
  useEffect(() => {
    clearBulkSelection();
  }, [clearBulkSelection, currentPage, filters, activeTab, sortField, sortDirection, pageSize, viewKey, mySuppliersOnly, mainSubTab]);

  const handleBulkDone = () => {
    clearBulkSelection();
    fetchData();
    setAppealsRefreshKey(k => k + 1);
  };

  /** Effective backend ordering field for a column (Qty Ordered is dynamic). */
  const effectiveSortField = (col: DeliveryColumnConfig): string => {
    if (col.id === 'qty_ordered') return scheduleCollabActive ? 'po_line_schedule__schedule_qty' : 'po_line__quantity';
    return col.sortField ?? col.id;
  };

  // Sortable header component
  const SortableHeader = ({ field, label, className = '', style }: { field: string; label: string; className?: string; style?: React.CSSProperties }) => (
    <th
      className={`py-3 px-4 font-medium cursor-pointer hover:bg-muted/50 transition-colors select-none ${className}`}
      style={style}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {sortField === field ? (
          sortDirection === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5 text-primary" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 text-primary" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50" />
        )}
      </div>
    </th>
  );

  // CR-3861: Export deliveries to CSV. Must mirror the list's filter
  // building exactly — the prior implementation only passed a handful
  // of filters AND incorrectly mapped `activeTab` to
  // `inspection_status`, which caused a 400 on every non-All tab
  // (Exceptions / Early-Late / On Time / Delivered This Week) and
  // dumped the wrong rows when it did succeed.
  // CR-6450: count of filters actually applied, for the supplier filter
  // header's badge. Counts applied `filters` (not the debounced text inputs)
  // so the badge matches what the grid is showing.
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (!value) return false;
    // `inspection_status` and `strategic` are not surfaced as filter controls.
    return key !== 'inspection_status' && key !== 'strategic';
  }).length;

  const handleExport = async () => {
    try {
      setExporting(true);
      const queryParams = buildDeliveriesExportParams({
        filters,
        activeTab,
        sortField,
        sortDirection,
        currentPage,
        pageSize,
        mySuppliersOnly,
        buyerCodes,
        viewParams: getViewQueryParams(),
      });

      const response = await purchasingAPI.exportGRNs(queryParams);

      // CR-7168: Filter and reorder CSV columns based on user's visible column config
      const rawCsv = await response.data.text();
      const filteredCsv = filterDeliveryCsvByVisibleColumns(rawCsv, columns);

      // Create download link
      const blob = new Blob([filteredCsv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deliveries_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Deliveries exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export deliveries');
    } finally {
      setExporting(false);
    }
  };

  const { formatDate: contextFormatDate, formatQuantity: contextFormatQuantity } = useFormatters();

  // Memoized formatters to prevent DeliveryRow re-renders
  const formatDate = useCallback((dateString: string | undefined) => {
    if (!dateString) return '-';
    return contextFormatDate(dateString);
  }, [contextFormatDate]);

  // CR-7262: this was the one correct quantity formatter in the codebase, and
  // the reason Deliveries showed 56.44 while everything else showed 56. It now
  // delegates to the shared one -- same output, except it reads the tenant's
  // separators instead of assuming en-GB. Kept inside useCallback: the
  // formatters here are memoised to stop DeliveryRow re-rendering.
  const formatQuantity = useCallback(
    (qty: string | number) => contextFormatQuantity(qty),
    [contextFormatQuantity],
  );

  if (loading && grns.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading deliveries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchData}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section Tour Trigger */}
      <div className="flex justify-end">
        <SectionTourTrigger
          pageKey="purchasing"
          sectionKey="delivery"
          variant="link"
          label="Tour this tab"
        />
      </div>

      {/* Sub-tabs: Deliveries | Submitted Appeals */}
      <div className="flex gap-2">
        <button
          onClick={() => setMainSubTab('deliveries')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            mainSubTab === 'deliveries'
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Deliveries
        </button>
        <button
          onClick={() => setMainSubTab('appeals')}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            mainSubTab === 'appeals'
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Submitted Appeals
          {/* CR-7167: Badge showing appeals awaiting response, so users know
              there's work to do without clicking into the tab first. Red + bold
              when > 0, matching the existing Action Required tab styling inside. */}
          {appealsActionCount > 0 && (
            <span className={`inline-flex items-center justify-center h-5 px-1.5 text-xs font-bold rounded-full ${
              mainSubTab === 'appeals'
                ? 'bg-white text-red-600'
                : 'bg-red-500 text-white'
            }`}>
              {appealsActionCount}
            </span>
          )}
        </button>
        {canSeeUnallocated && (
          <button
            onClick={() => setMainSubTab('unallocated')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              mainSubTab === 'unallocated'
                ? 'bg-green-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Unallocated GRNs
          </button>
        )}
        <div className="ml-auto">
          <MySuppliersToggle myOnly={mySuppliersOnly} onChange={(v) => { setMySuppliersOnly(v); setCurrentPage(1); }} codes={buyerCodes} />
        </div>
      </div>

      {mainSubTab === 'deliveries' && (<>
      {/* Filter Section. CR-6450 did this for the supplier view; CR-6442
          rework extends it to the customer view, which QA failed for the
          mismatch. The panel is always mounted so its header (count badge +
          Refresh / Export / Hide filters) stays visible when collapsed — only
          the sections and footer hide, as the design note requires. */}
      <DeliveryFilterSection
          filters={filters}
          textInputs={textInputs}
          pendingDates={pendingDates}
          siteOptions={siteOptions}
          supplierGroupOptions={supplierGroupOptions}
          commodityGroupOptions={commodityGroupOptions}
          commodityTypeOptions={commodityTypeOptions}
          supplierOptions={supplierOptions}
          divisionOptions={divisionOptions}
          supplierCountryOptions={supplierCountryOptions}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          onSearch={handleSearch}
          onHideFilters={() => setShowFilters(false)}
          supplierLayout={isSupplierView}
          expanded={showFilters}
          onShowFilters={() => setShowFilters(true)}
          onRefresh={fetchData}
          onExport={handleExport}
          exporting={exporting}
          activeFilterCount={activeFilterCount}
        />

      {/* Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-rose-600" />
              <h3 className="font-semibold">Deliveries</h3>
              <span className="text-sm text-muted-foreground">
                {totalCount.toLocaleString()} {totalCount === 1 ? 'delivery' : 'deliveries'}
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {/* Always-visible quick searches (GRN number, part number, supplier
                  name) — the same debounced text filters as the Show Filters panel. */}
              <DeliveryQuickSearch
                values={{ grnNumber: textInputs.grnNumber, partNumber: textInputs.partNumber, supplierName: textInputs.supplierName }}
                showSupplier={!isSupplierView}
                onChange={handleFilterChange}
              />
              {/* CR-6442 rework: the show/hide control lives in the filter
                  header for both views now. Keeping a copy here as well is
                  what QA saw as "2 hide filters buttons on page". */}
              {/* Column manager: show/hide + drag-reorder, persisted per user. */}
              <DeliveryColumnsDropdown
                columns={columns}
                availableIds={availableIds}
                onToggle={toggleColumn}
                onReset={resetColumns}
                onSelectAll={selectAllCols}
                onReorder={reorderColumns}
              />
              {/* CR-3861 / CR-6450: the Export button lives in the filter
                  header (always mounted) for both views — a second copy here
                  read as two "Export" buttons on the customer page. */}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList>
                <TabsTrigger value="all">
                  All{tabCounts['all'] !== undefined ? ` (${tabCounts['all'].toLocaleString()})` : ''}
                </TabsTrigger>
                {/* Last 4 Weeks tab — deliveries received in the past 28 days
                    (by receipt date). Replaced the Exceptions quick filter. */}
                <TabsTrigger value="last_4_weeks">
                  Last 4 Weeks{tabCounts['last_4_weeks'] !== undefined ? ` (${tabCounts['last_4_weeks'].toLocaleString()})` : ''}
                </TabsTrigger>
                {/* Early & Late — early/late deliveries, latest receipt first
                    (the sort is forced in handleTabChange). Always visible. */}
                <TabsTrigger value="early_late" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                  Early &amp; Late{tabCounts['early_late'] !== undefined ? ` (${tabCounts['early_late'].toLocaleString()})` : ''}
                </TabsTrigger>
                {/* On Time filters by GRN-level on_time_status, which is
                    undefined on schedule-aware tenants — hidden there. */}
                {!scheduleCollabActive && (
                  <TabsTrigger value="on_time">
                    On Time{tabCounts['on_time'] !== undefined ? ` (${tabCounts['on_time'].toLocaleString()})` : ''}
                  </TabsTrigger>
                )}
                <TabsTrigger value="delivered_this_week">
                  Delivered This Week{tabCounts['delivered_this_week'] !== undefined ? ` (${tabCounts['delivered_this_week'].toLocaleString()})` : ''}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Deliveries Table - 25 columns matching v1.89 */}
      <Card data-tour="grn-table">
        <CardContent className="p-0">
          {grns.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No deliveries found matching your criteria.
            </div>
          ) : (
            <>
              {/* Top Pagination */}
              <div className="px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize} servedPerPage={servedPageRef.current?.served}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemLabel="deliveries"
                />
              </div>

              {/* CR-7420 / CR-8214: bulk action bar — shown when rows are selected. */}
              {bulkModeActive && (
                <DeliveryBulkBar bulk={bulk} isSupplierView={isSupplierView} onDone={handleBulkDone} />
              )}

              {/* CR-3866: Added max-height and sticky header support */}
              <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)]">
                <table className="w-full text-sm" style={{ minWidth: `${tableMinWidth}px` }}>
                  <thead className="sticky top-0 z-20">
                    <tr className="border-b bg-muted/80 backdrop-blur-sm">
                      {/* Bulk-appeal select-all header — multi-select, either side. */}
                      {bulkModeActive && (
                        <th className="text-left py-3 px-4 font-medium">
                          <input
                            type="checkbox"
                            checked={bulk.allEligibleSelected}
                            onChange={bulk.toggleAll}
                            disabled={bulk.eligibleGrns.length === 0}
                            className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            title="Select all eligible deliveries on this page"
                          />
                        </th>
                      )}
                      {/* Config-driven headers: order + visibility come from the
                          column manager; On-Time Status / Delivery Date default
                          to sit right after Appeal Status (customer 2026-07-15). */}
                      {visibleCols.map((col) => {
                        const align = col.align === 'right' ? 'text-right' : 'text-left';
                        const style = { minWidth: col.minWidth };
                        if (col.sortable) {
                          return (
                            <SortableHeader
                              key={col.id}
                              field={effectiveSortField(col)}
                              label={col.label}
                              className={align}
                              style={style}
                            />
                          );
                        }
                        return (
                          <th key={col.id} className={`${align} py-3 px-4 font-medium`} style={style}>{col.label}</th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {/* One row per GoodsReceiptNote record (= one per PO
                        line within a physical delivery note). Per-schedule
                        allocation detail lives in the modal's Schedule
                        Allocation tab. */}
                    {grns.map((grn: GoodsReceiptNote) => {
                      return (
                        <DeliveryRow
                          key={String(grn.id)}
                          grn={grn}
                          visibleColumns={visibleCols}
                          isSupplier={isSupplier}
                          scheduleCollabActive={scheduleCollabActive}
                          hideAppealForSupplier={hideAppealForSupplier}
                          canReassign={canReassign}
                          formatDate={formatDate}
                          formatQuantity={formatQuantity}
                          getOntimeStatusBadge={getOntimeStatusBadge}
                          getAppealStatusBadge={getAppealStatusBadge}
                          onViewDetails={handleViewDetails}
                          onReassign={setReassignGrn}
                          onOpenAppealResponse={openAppealResponseForGrn}
                          onAcknowledge={setAcknowledgeGrn}
                          onAppeal={setAppealGrn}
                          onCancelAppeal={handleCancelAppeal}
                          bulkMode={bulkModeActive}
                          selectable={bulk.isSelectable(grn)}
                          selected={bulk.selectedIds.has(grn.id)}
                          onToggleSelect={bulk.toggle}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Bottom Pagination */}
              <div className="px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize} servedPerPage={servedPageRef.current?.served}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemLabel="deliveries"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
      </>)}

      {mainSubTab === 'appeals' && (
        <AppealsSubTab
          formatDate={formatDate}
          getAppealStatusBadge={getAppealStatusBadge}
          refreshKey={appealsRefreshKey}
          allowBulkRespond={!isSupplier && deliveryAppealBulkEnabled}
          canRespond={!isSupplier}
          // CR-7167: Pre-select Action Required tab when deep-linked from dashboard
          initialTab={urlAppealTab === 'action_required' ? 'action_required' : 'all'}
          supplierOptions={supplierOptions}
          onViewGrn={async (grnId) => {
            if (!grnId) {
              toast.error('No GRN ID on this appeal');
              return;
            }
            try {
              const res = await purchasingAPI.getGRN(grnId);
              setSelectedGrn(res.data);
              setShowModal(true);
            } catch (err: unknown) {
              console.error('Failed to fetch GRN:', err);
              toast.error('Failed to load delivery details');
            }
          }}
          onRespondAppeal={async (appealId) => {
            // CR-4651 rework P7: re-fetch the appeal so the response modal
            // gets the freshest copy (status may have changed since the row
            // was rendered).
            try {
              const res = await purchasingAPI.getDeliveryAppeal(appealId);
              setRespondAppeal(res.data);
            } catch (err: unknown) {
              toast.error(getApiErrorMessage(err, 'Failed to load appeal details'));
            }
          }}
        />
      )}

      {mainSubTab === 'unallocated' && canSeeUnallocated && (
        <UnallocatedGRNsTab
          onViewGrn={async (grnId) => {
            try {
              const res = await purchasingAPI.getGRN(grnId);
              setSelectedGrn(res.data);
              setShowModal(true);
            } catch (err: unknown) {
              console.error('Failed to fetch GRN:', err);
              toast.error('Failed to load delivery details');
            }
          }}
        />
      )}

      {loading && (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {/* Delivery Details Modal */}
      {showModal && selectedGrn && (
        <DeliveryDetailsModal
          grn={selectedGrn}
          onClose={() => {
            setShowModal(false);
            setSelectedGrn(null);
          }}
          onRefresh={() => {
            fetchData();
            // Mutations inside the modal (raise/respond appeal, dispute,
            // acknowledge) all touch appeal state, so re-pull the appeals
            // subtab data too — otherwise switching to that tab afterwards
            // shows stale rows until F5.
            setAppealsRefreshKey(k => k + 1);
          }}
        />
      )}
      {/* Manual GRN re-assignment to a schedule line — superusers and staff
          (Scheduled installs), matching the row button + backend gate. */}
      {reassignGrn && canReassign && (
        <LinkScheduleModal
          grn={reassignGrn}
          onClose={() => setReassignGrn(null)}
          onLinked={() => { fetchData(); }}
        />
      )}
      {/* CR-4602: Direct appeal modal from Action column */}
      {appealGrn && (
        <RaiseAppealModal
          isOpen={true}
          grn={appealGrn}
          onClose={() => setAppealGrn(null)}
          onSuccess={() => {
            setAppealGrn(null);
            fetchData();
            setAppealsRefreshKey(k => k + 1);
          }}
        />
      )}
      {/* CR-4660: Direct acknowledge modal (skips details popup) */}
      {acknowledgeGrn && (
        <AcknowledgeGRNModal
          isOpen={true}
          onClose={() => setAcknowledgeGrn(null)}
          grnId={acknowledgeGrn.id}
          grnNumber={acknowledgeGrn.grn_number}
          partNumber={acknowledgeGrn.part_number || ''}
          poNumber={acknowledgeGrn.po_number || ''}
          onSubmit={handleAcknowledgeSubmit}
        />
      )}

      {/* CR-4786: Respond-to-appeal modal for customer replies to supplier appeals */}
      {respondAppeal && (
        <AppealResponseModal
          isOpen={true}
          onClose={() => setRespondAppeal(null)}
          appeal={respondAppeal}
          onSuccess={(action) => {
            // Optimistic patch so the row's badge/buttons update immediately,
            // before the network refetch lands. action -> appeal status:
            // accept -> accepted, query -> queried, reject -> rejected.
            const nextStatus: Record<'accept' | 'query' | 'reject', 'accepted' | 'queried' | 'rejected'> = {
              accept: 'accepted',
              query: 'queried',
              reject: 'rejected',
            };
            const appealId = respondAppeal.id;
            const newStatus = nextStatus[action];
            setGrns(prev => prev.map(g => {
              if (!g.latest_appeal || g.latest_appeal.id !== appealId) return g;
              return {
                ...g,
                latest_appeal: { ...g.latest_appeal, status: newStatus },
              };
            }));
            setRespondAppeal(null);
            fetchData();
            // Force the appeals subtab to re-pull its list + counts as well —
            // its state lives inside AppealsSubTab so fetchData() doesn't reach it.
            setAppealsRefreshKey(k => k + 1);
          }}
        />
      )}
    </div>
  );
}
